import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Generic multi-select with a popover + checkbox list.
 *
 * Props:
 *  - options: [{ value, label }]
 *  - value:   string[]
 *  - onChange(string[])
 *  - placeholder
 *  - testId
 */
export default function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Selecciona…",
  testId,
  emptyLabel = "Ninguno",
  className,
}) {
  const [open, setOpen] = useState(false);
  const set = new Set(value || []);
  const toggle = (v) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  };

  const selectedLabels = options.filter((o) => set.has(o.value)).map((o) => o.label);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            "h-auto min-h-9 w-full justify-between px-2 py-1.5 text-left font-normal",
            className
          )}
        >
          <div className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden">
            {selectedLabels.length === 0 ? (
              <span className="text-muted-foreground text-sm">{placeholder}</span>
            ) : (
              selectedLabels.slice(0, 4).map((l) => (
                <Badge key={l} variant="secondary" className="font-normal text-xs">
                  {l}
                </Badge>
              ))
            )}
            {selectedLabels.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{selectedLabels.length - 4}
              </Badge>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <ScrollArea className="max-h-72">
          <div className="p-1">
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">{emptyLabel}</div>
            )}
            {options.map((o) => {
              const isSelected = set.has(o.value);
              return (
                <button
                  type="button"
                  key={o.value}
                  data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent"
                >
                  <Checkbox checked={isSelected} className="pointer-events-none" />
                  <span className="flex-1 truncate">{o.label}</span>
                  {isSelected && <Check className="h-3.5 w-3.5 opacity-70" />}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
